/**
 * RulesConfigStep - Step 1 of onboarding wizard
 * Configure prioritization rules before first upload
 */

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Clock, AlertTriangle, RotateCcw, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../../shadcn/button';
import { useClinicRules } from '../../../hooks/useClinicRules';
import RuleCard from './RuleCard';

// Rule display configuration
// Rules that are "always on" and shouldn't be shown as toggleable
const ALWAYS_ON_RULES = ['flag_urgent_status', 'flag_concerning_status'];

const RULE_CONFIG = {
  overdue_appointment: {
    icon: Clock,
    iconColor: 'text-amber-600',
    iconBgColor: 'bg-amber-100',
    order: 1,
  },
  missed_visits_flag: {
    icon: AlertTriangle,
    iconColor: 'text-red-600',
    iconBgColor: 'bg-red-100',
    order: 2,
  },
  escalate_no_response: {
    icon: RotateCcw,
    iconColor: 'text-orange-600',
    iconBgColor: 'bg-orange-100',
    order: 3,
  },
};

const RulesConfigStep = ({ onNext, onUseDefaults }) => {
  const { rules, loading, updateRule } = useClinicRules();
  const [localEdits, setLocalEdits] = useState({});
  const [saving, setSaving] = useState(false);

  // Filter to risk rules only, excluding always-on rules
  const riskRules = useMemo(() => {
    return rules
      .filter(r => r.category === 'risk' && !ALWAYS_ON_RULES.includes(r.code))
      .sort((a, b) => {
        const orderA = RULE_CONFIG[a.code]?.order || 99;
        const orderB = RULE_CONFIG[b.code]?.order || 99;
        return orderA - orderB;
      });
  }, [rules]);

  // Initialize local edits from fetched rules
  useEffect(() => {
    if (riskRules.length > 0 && Object.keys(localEdits).length === 0) {
      const edits = {};
      riskRules.forEach(rule => {
        edits[rule.id] = {
          enabled: rule.enabled,
          config: { ...rule.config },
        };
      });
      setLocalEdits(edits);
    }
  }, [riskRules, localEdits]);

  // Update local state
  const updateLocalRule = (ruleId, field, value) => {
    setLocalEdits(prev => ({
      ...prev,
      [ruleId]: {
        ...prev[ruleId],
        [field]: value,
      },
    }));
  };

  const updateLocalConfig = (ruleId, configKey, value) => {
    setLocalEdits(prev => ({
      ...prev,
      [ruleId]: {
        ...prev[ruleId],
        config: {
          ...prev[ruleId]?.config,
          [configKey]: value,
        },
      },
    }));
  };

  // Save all rules and proceed
  const handleNext = async () => {
    // Cross-field validation for overdue_appointment thresholds
    for (const [, edits] of Object.entries(localEdits)) {
      if (edits.enabled && (edits.config?.high_days != null || edits.config?.medium_days != null)) {
        const high = edits.config?.high_days;
        const medium = edits.config?.medium_days;
        if (high === '' || medium === '' || !Number.isFinite(Number(high)) || !Number.isFinite(Number(medium))) {
          toast.error('Please enter valid values for risk thresholds.');
          return;
        }
        if (Number(high) <= Number(medium)) {
          toast.error('High risk threshold must be greater than medium risk threshold.');
          return;
        }
      }
    }

    setSaving(true);
    try {
      // Save all edited rules
      const updates = Object.entries(localEdits).map(([ruleId, edits]) => {
        return updateRule(ruleId, {
          enabled: edits.enabled,
          config: edits.config,
        });
      });

      await Promise.all(updates);
      onNext();
    } catch (err) {
      console.error('Failed to save rules:', err);
    } finally {
      setSaving(false);
    }
  };

  // Check for validation errors across all edits (used to disable Next button)
  const hasValidationError = Object.values(localEdits).some((edits) => {
    if (!edits.enabled) return false;
    const high = edits.config?.high_days;
    const medium = edits.config?.medium_days;
    if (high == null && medium == null) return false;
    if (high === '' || medium === '') return true;
    return Number(high) <= Number(medium);
  });

  // Render config inputs for specific rules
  const renderRuleConfig = (rule) => {
    const edit = localEdits[rule.id];
    if (!edit?.enabled) return null;

    switch (rule.code) {
      case 'overdue_appointment': {
        const highDays = edit.config?.high_days;
        const mediumDays = edit.config?.medium_days;
        const bothFilled = highDays !== '' && highDays != null && mediumDays !== '' && mediumDays != null;
        const thresholdError = bothFilled && Number(highDays) <= Number(mediumDays);
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <input
                type="number"
                min="1"
                value={highDays ?? 90}
                onChange={(e) => updateLocalConfig(rule.id, 'high_days', e.target.value === '' ? '' : parseInt(e.target.value))}
                className={`w-14 px-2 py-1 text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent ${thresholdError ? 'border-red-300' : 'border-slate-200'}`}
              />
              <span>days</span>
              <span className="text-red-600 font-medium">High Risk</span>
              <span className="text-slate-300 mx-1">·</span>
              <input
                type="number"
                min="1"
                value={mediumDays ?? 30}
                onChange={(e) => updateLocalConfig(rule.id, 'medium_days', e.target.value === '' ? '' : parseInt(e.target.value))}
                className={`w-14 px-2 py-1 text-center border rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent ${thresholdError ? 'border-red-300' : 'border-slate-200'}`}
              />
              <span>days</span>
              <span className="text-amber-600 font-medium">Medium Risk</span>
            </div>
            {thresholdError && (
              <p className="text-xs text-red-600">High risk days must be greater than medium risk days.</p>
            )}
          </div>
        );
      }

      case 'missed_visits_flag':
        return (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Flag after</span>
            <input
              type="number"
              min="1"
              value={edit.config?.threshold || 3}
              onChange={(e) => updateLocalConfig(rule.id, 'threshold', parseInt(e.target.value) || 3)}
              className="w-14 px-2 py-1 text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            <span>missed appointments</span>
          </div>
        );

      case 'escalate_no_response':
        return (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Resurface after</span>
            <input
              type="number"
              min="1"
              value={edit.config?.days || 3}
              onChange={(e) => updateLocalConfig(rule.id, 'days', parseInt(e.target.value) || 3)}
              className="w-14 px-2 py-1 text-center border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
            <span>days with no reply</span>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto mb-4" />
        <p className="text-sm text-slate-500">Loading rules...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-xl font-bold text-slate-900 mb-2">
          Set up your prioritization rules
        </h1>
        <p className="text-sm text-slate-500">
          These run automatically on every upload
        </p>
      </div>

      {/* Editable Rules */}
      <div className="space-y-3">
        {riskRules.map((rule) => {
          const config = RULE_CONFIG[rule.code] || {};
          const edit = localEdits[rule.id];

          return (
            <RuleCard
              key={rule.id}
              icon={config.icon || AlertTriangle}
              iconColor={config.iconColor}
              iconBgColor={config.iconBgColor}
              name={rule.name}
              enabled={edit?.enabled ?? rule.enabled}
              onToggle={(val) => updateLocalRule(rule.id, 'enabled', val)}
            >
              {renderRuleConfig(rule)}
            </RuleCard>
          );
        })}

        {/* EHR Detection - Always on, core functionality */}
        <RuleCard
          icon={Sparkles}
          iconColor="text-blue-600"
          iconBgColor="bg-blue-100"
          name="EHR Signal Detection"
          description='We detect flags like "Overdue PSA" and statuses like "callback", "no show"'
          isInfoOnly
        />
      </div>

      {/* Footer */}
      <p className="text-xs text-slate-400 text-center">
        You can adjust these anytime in Settings → Rules
      </p>

      {/* Actions */}
      <div className="space-y-3">
        <Button
          onClick={handleNext}
          disabled={saving || hasValidationError}
          className="w-full bg-slate-900 hover:bg-slate-800 text-white py-5 text-base"
        >
          {saving ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Next: Upload Your List
              <ArrowRight className="h-5 w-5 ml-2" />
            </>
          )}
        </Button>

        <button
          type="button"
          onClick={onUseDefaults}
          className="w-full text-sm text-slate-500 hover:text-slate-700 py-2"
        >
          Use defaults →
        </button>
      </div>
    </div>
  );
};

export default RulesConfigStep;
